import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Download, KeyRound, Laptop } from 'lucide-react';
import { Link } from 'react-router-dom';
import { agentDownloadsApi, endpointAgentsApi, findingsApi } from '../lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { cn } from '../lib/cn';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import type { EndpointAgentToken } from '../lib/types';

/** Anchor target for the empty-state's "go to the download section" link below. */
const CONNECT_SECTION_ID = 'endpoint-testing-connect';

function StatusDot({ connected }: { connected: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn('inline-block h-2 w-2 rounded-full', connected ? 'bg-success' : 'bg-ink-faint')} />
      {connected ? 'Connected' : 'Disconnected'}
    </span>
  );
}

function TokenReveal({ token, expiresAt }: EndpointAgentToken) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 rounded-xl border-2 border-brand bg-surface-sunken px-3 py-2">
        <code className="truncate text-sm text-ink">{token}</code>
        <Button variant="ghost" size="sm" onClick={copy}>
          <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <p className="text-xs font-medium text-brand">
        Save this token — it will only be shown once. Expires in 24 hours.
      </p>
      <p className="text-xs text-ink-faint">Token expires {new Date(expiresAt).toLocaleString()}.</p>
    </div>
  );
}

/**
 * OS-specific install steps (Task 1A) — the endpoint-page equivalent of InternalTesting.tsx's
 * InstallInstructions, adapted to this page's installer formats (.msi/.pkg) and its "runs daily
 * checks automatically" framing rather than "connects automatically". Always visible next to the
 * download buttons, not gated behind an issued token, same reasoning as InternalTesting.tsx.
 */
function InstallInstructions() {
  return (
    <div className="grid gap-4 text-sm text-ink-muted sm:grid-cols-2">
      <div>
        <p className="font-medium text-ink">Windows:</p>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>Download defendiq-agent-win-endpoint.exe</li>
          <li>Right-click → Run as Administrator</li>
          <li>Enter your registration token when prompted</li>
          <li>
            Done — agent installs itself and starts automatically. You will see a notification confirming it is running. It will start
            automatically after every reboot.
          </li>
        </ol>
      </div>
      <div>
        <p className="font-medium text-ink">Linux:</p>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>Download defendiq-agent-linux</li>
          <li>chmod +x defendiq-agent-linux</li>
          <li>sudo ./defendiq-agent-linux --endpoint</li>
          <li>Enter your registration token when prompted</li>
          <li>Done — agent installs itself as a system service and starts automatically after every reboot.</li>
        </ol>
      </div>
    </div>
  );
}

/** One agent row's Findings-column cell: a per-hostname count fetched via useQueries below,
 * linking into the Findings page pre-filtered to this agent's hostname (see Findings.tsx's
 * ?target= support). */
function FindingsCell({ hostname, count, isLoading }: { hostname: string; count: number | undefined; isLoading: boolean }) {
  if (isLoading || count === undefined) return <span className="text-ink-faint">…</span>;
  return (
    <Link to={`/findings?target=${encodeURIComponent(hostname)}`} className="font-medium text-brand underline-offset-2 hover:underline">
      {count} finding{count === 1 ? '' : 's'} →
    </Link>
  );
}

const WINDOWS_CHECKS = [
  'Windows Firewall status',
  'BitLocker encryption',
  'Antivirus status',
  'Windows Update age (> 30 days = HIGH)',
  'RDP exposure',
  'Local admin accounts',
  'Password policy',
  'WDigest credential exposure',
  'LSA protection',
  'Credential Guard',
  'AlwaysInstallElevated (privilege escalation)',
  'Unquoted service paths',
  'WinRM enabled',
  'SMB signing',
  'Open ports',
  'Scheduled tasks inventory',
  'Run key inventory (persistence)',
  'Installed software CVEs (via NVD)',
] as const;

const LINUX_CHECKS = [
  'Firewall (ufw/iptables)',
  'SSH config (PermitRootLogin, PasswordAuthentication)',
  'Disk encryption (LUKS)',
  'Auto-updates configured',
  'SUID binaries',
  'Cron jobs running as root',
  'World-writable files',
  'Sudo NOPASSWD',
  'Open ports',
  'Failed login attempts',
] as const;

function CheckList({ items }: { items: readonly string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2 text-sm">
          <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-success-soft text-success">
            <Check size={11} strokeWidth={3} />
          </span>
          <span className="text-ink-muted">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function EndpointChecksSection() {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Endpoint checks</CardTitle>
        <CardDescription>What the agent tests on each platform.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">Windows</p>
            <CheckList items={WINDOWS_CHECKS} />
          </div>
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">Linux</p>
            <CheckList items={LINUX_CHECKS} />
          </div>
        </div>
        <p className="mt-4 text-xs text-ink-faint">
          Checks run automatically on agent install and repeat daily. Results appear in Findings.
        </p>
      </CardContent>
    </Card>
  );
}

export function EndpointTesting() {
  const { principal } = useAuth();
  const canManage = principal?.role === undefined || can(principal.role, 'endpointAgent:manage');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [issuedToken, setIssuedToken] = useState<EndpointAgentToken | null>(null);

  const query = useQuery({
    queryKey: ['endpoint-agents'],
    queryFn: () => endpointAgentsApi.list(),
    refetchInterval: 30000,
  });

  // Fetched on mount — GET /v1/agent/downloads is public/unauthenticated (see
  // apps/api/src/routes/agent-downloads.ts), returning the raw windows/linux agent binaries built
  // by apps/agent's pkg:* scripts. This page uses `windowsEndpoint` specifically (a build that
  // always runs in endpoint mode) rather than the shared `windows` field used by InternalTesting —
  // a double-clicked .exe has no CLI flag to select endpoint mode, so a dedicated build is required
  // to avoid it registering against the internal-agent endpoint and 401ing.
  const downloadsQuery = useQuery({
    queryKey: ['agent-downloads'],
    queryFn: () => agentDownloadsApi.get(),
  });
  const downloads = downloadsQuery.data;

  const tokenMutation = useMutation({
    mutationFn: () => endpointAgentsApi.generateToken(),
    onSuccess: (result) => setIssuedToken(result),
    onError: (err) => toast({ title: 'Could not generate token', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => endpointAgentsApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['endpoint-agents'] });
      toast({ title: 'Agent revoked', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not revoke agent', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => endpointAgentsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['endpoint-agents'] });
      toast({ title: 'Agent removed', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not remove agent', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const agents = query.data?.agents ?? [];

  // One findings-count query per connected agent, keyed by hostname — mirrors the
  // multi-query (useQueries) pattern Findings.tsx/InternalTesting.tsx already use for
  // per-engagement fetches.
  const findingsQueries = useQueries({
    queries: agents.map((agent) => ({
      queryKey: ['findings', 'tenant-wide', agent.hostname],
      queryFn: () => findingsApi.listForTenant(agent.hostname),
      enabled: agents.length > 0,
    })),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  return (
    <div>
      <Card id={CONNECT_SECTION_ID} className="mb-6">
        <CardHeader>
          <CardTitle>Connect an endpoint</CardTitle>
          <CardDescription>
            Install the DefendIQ agent on any workstation to enable endpoint testing. The agent runs daily checks
            automatically and reports findings back to your workspace.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {downloads?.windowsEndpoint ? (
              <Button asChild variant="outline">
                <a href={downloads.windowsEndpoint}>
                  <Download size={16} /> Download for Windows (.msi)
                </a>
              </Button>
            ) : (
              <Button variant="outline" disabled title="Download not yet available">
                <Download size={16} /> Download for Windows (.msi)
              </Button>
            )}
            {/* Same raw agent binary + endpoint URL as InternalTesting.tsx (GET /v1/agent/downloads'
                `linux` field) — the Linux download is the same apps/agent binary either way, just
                invoked with --endpoint (see InstallInstructions below) instead of the default mode. */}
            {downloads?.linux ? (
              <Button asChild variant="outline">
                <a href={downloads.linux}>
                  <Download size={16} /> Download for Linux (.sh)
                </a>
              </Button>
            ) : (
              <Button variant="outline" disabled title="Download not yet available">
                <Download size={16} /> Download for Linux (.sh)
              </Button>
            )}
          </div>

          <InstallInstructions />

          {canManage ? (
            issuedToken ? (
              <TokenReveal token={issuedToken.token} expiresAt={issuedToken.expiresAt} />
            ) : (
              <Button onClick={() => tokenMutation.mutate()} disabled={tokenMutation.isPending}>
                <KeyRound size={16} /> Generate registration token
              </Button>
            )
          ) : null}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Connected endpoints</CardTitle>
        </CardHeader>
        <CardContent>
          {agents.length === 0 ? (
            <EmptyState
              icon={<Laptop size={32} />}
              title="No endpoints connected yet"
              description="Install the agent to get started."
              action={
                <a href={`#${CONNECT_SECTION_ID}`} className="text-sm font-medium text-brand underline-offset-2 hover:underline">
                  Go to the download section above
                </a>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-ink-faint">
                    <th className="py-2 pr-4 font-medium">Hostname</th>
                    <th className="py-2 pr-4 font-medium">OS</th>
                    <th className="py-2 pr-4 font-medium">OS Version</th>
                    <th className="py-2 pr-4 font-medium">Last seen</th>
                    <th className="py-2 pr-4 font-medium">Findings</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    {/* Deviation from the ticket's exact column list: an Actions/Revoke column is
                        added anyway, gated to Owner/Admin, mirroring InternalTesting.tsx — a lost
                        or compromised workstation's agent credential should be revocable from
                        this page, the same low-cost necessity the backend route's own docstring
                        (apps/api/src/routes/endpoint-agents.ts) already called out. */}
                    {canManage ? <th className="py-2 pr-4 font-medium">Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent, index) => (
                    <tr key={agent.id} className="border-b border-border last:border-none">
                      <td className="py-3 pr-4 font-medium text-ink">{agent.hostname}</td>
                      <td className="py-3 pr-4 text-ink-muted">{agent.os}</td>
                      <td className="py-3 pr-4 text-ink-muted">{agent.osVersion}</td>
                      <td className="py-3 pr-4 text-ink-muted">
                        {agent.lastSeen ? new Date(agent.lastSeen).toLocaleString() : 'Never'}
                      </td>
                      <td className="py-3 pr-4">
                        <FindingsCell
                          hostname={agent.hostname}
                          count={findingsQueries[index]?.data?.findings.length}
                          isLoading={findingsQueries[index]?.isLoading ?? false}
                        />
                      </td>
                      <td className="py-3 pr-4 text-ink-muted">
                        <StatusDot connected={agent.status === 'connected'} />
                      </td>
                      {canManage ? (
                        <td className="py-3 pr-4">
                          <div className="flex gap-2">
                            {!agent.revokedAt ? (
                              <ConfirmDialog
                                trigger={
                                  <Button size="sm" variant="danger">
                                    Revoke
                                  </Button>
                                }
                                title="Revoke this endpoint?"
                                description={`"${agent.hostname}" will immediately lose access — endpoint testing on this machine will stop. This cannot be undone; you'd need to install and register the agent again.`}
                                confirmLabel="Revoke agent"
                                variant="danger"
                                onConfirm={() => revokeMutation.mutateAsync(agent.id)}
                              />
                            ) : null}
                            {agent.status !== 'connected' ? (
                              <ConfirmDialog
                                trigger={
                                  <Button size="sm" variant="outline">
                                    Delete
                                  </Button>
                                }
                                title={`Remove ${agent.hostname} from your workspace?`}
                                description="This cannot be undone."
                                confirmLabel="Remove agent"
                                variant="danger"
                                onConfirm={() => deleteMutation.mutateAsync(agent.id)}
                              />
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <EndpointChecksSection />
    </div>
  );
}
